import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Paginated horizontal carousel.
 *
 * - `items`: array of data
 * - `renderItem(item, index)`: render fn
 * - `perPage`: desktop tile count per page (responsive — see below)
 * - `gap`: px between tiles
 * - `arrowsColor`: stroke color for the arrows (default gold)
 *
 * On screens < 480px we automatically show 2 per page; 480–768 → 3; ≥768 → `perPage`.
 * Pagination is index-based — sliding by full pages with smooth scroll.
 */
export default function Carousel({
  items = [], renderItem, perPage = 4, gap = 10,
  arrowsColor = "var(--ang-gold)", testId = "carousel",
}) {
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  const [responsivePerPage, setResponsivePerPage] = useState(perPage);

  useEffect(() => {
    const calc = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 1024;
      if (w < 480) setResponsivePerPage(2);
      else if (w < 768) setResponsivePerPage(3);
      else setResponsivePerPage(perPage);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [perPage]);

  const totalPages = Math.max(1, Math.ceil(items.length / responsivePerPage));
  const safePage = Math.min(page, totalPages - 1);

  const scrollToPage = (p) => {
    if (!trackRef.current) return;
    const next = Math.max(0, Math.min(totalPages - 1, p));
    setPage(next);
    const el = trackRef.current;
    const child = el.children[0];
    if (!child) return;
    const pageWidth = (child.clientWidth + gap) * responsivePerPage;
    el.scrollTo({ left: next * pageWidth, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <div data-testid={testId} style={{ position: "relative" }}>
      <div
        ref={trackRef}
        style={{
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: `calc((100% - ${gap * (responsivePerPage - 1)}px) / ${responsivePerPage})`,
          gap: `${gap}px`,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",  // Firefox
          msOverflowStyle: "none", // IE
          paddingBottom: "4px",
        }}
        onScroll={(e) => {
          // Sync page state when user swipes
          const el = e.target;
          const child = el.children[0];
          if (!child) return;
          const pageWidth = (child.clientWidth + gap) * responsivePerPage;
          if (pageWidth > 0) {
            const p = Math.round(el.scrollLeft / pageWidth);
            if (p !== safePage) setPage(p);
          }
        }}
      >
        {items.map((item, idx) => (
          <div key={idx} style={{ scrollSnapAlign: "start" }}>
            {renderItem(item, idx)}
          </div>
        ))}
        <style>{`[data-testid="${testId}"] > div::-webkit-scrollbar { display: none; }`}</style>
      </div>

      {/* Arrows — only show if there's more than one page */}
      {totalPages > 1 && (
        <>
          {safePage > 0 && (
            <button
              type="button"
              data-testid={`${testId}-prev`}
              onClick={() => scrollToPage(safePage - 1)}
              style={arrowStyle("left", arrowsColor)}
              aria-label="Назад"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          {safePage < totalPages - 1 && (
            <button
              type="button"
              data-testid={`${testId}-next`}
              onClick={() => scrollToPage(safePage + 1)}
              style={arrowStyle("right", arrowsColor)}
              aria-label="Вперёд"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </>
      )}

      {/* Dots */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginTop: "10px" }}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`${testId}-dot-${i}`}
              onClick={() => scrollToPage(i)}
              style={{
                width: i === safePage ? "18px" : "6px",
                height: "6px",
                borderRadius: "3px",
                background: i === safePage ? arrowsColor : "var(--ang-text-faint)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function arrowStyle(side, color) {
  return {
    position: "absolute",
    top: "50%",
    [side]: "-6px",
    transform: "translateY(-50%)",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "var(--ang-nav-bg)",
    border: "1px solid var(--ang-border)",
    color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 5,
    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
    backdropFilter: "blur(8px)",
  };
}
